import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FunnelIcon, 
  Squares2X2Icon,
  ListBulletIcon,
  CubeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Product, Category, PaginatedResponse } from '../types';
import { ApiService } from '../services/api';

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGridView, setIsGridView] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 필터 상태
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedPaymentType, setSelectedPaymentType] = useState(searchParams.get('paymentType') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  
  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, selectedPaymentType, sortBy, searchQuery, currentPage]);

  const loadCategories = async () => {
    try {
      const response = await ApiService.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      
      const params = {
        page: currentPage,
        limit: 12,
        category: selectedCategory,
        search: searchQuery,
        sortBy: sortBy,
        sortOrder: 'desc' as const,
        paymentType: selectedPaymentType
      };

      // 빈 값들 제거
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== '' && value !== undefined)
      );

      const response: PaginatedResponse<Product> = await ApiService.getProducts(cleanParams);
      
      if (response.success) {
        setProducts(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        }
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
    updateURL();
  };

  const updateURL = () => {
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedPaymentType) params.set('paymentType', selectedPaymentType);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    if (searchQuery) params.set('q', searchQuery);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    setSearchParams(params);
  };

  const formatPrice = (cashPrice: number, goldPrice: number) => {
    const parts = [];
    if (cashPrice > 0) parts.push(`${cashPrice.toLocaleString()} CP`);
    if (goldPrice > 0) parts.push(`${goldPrice.toLocaleString()} GP`);
    return parts.join(' + ') || '무료';
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'cash_only':
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Cash Only</span>;
      case 'gold_only':
        return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">Gold Only</span>;
      case 'mixed':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Mixed</span>;
      default:
        return null;
    }
  };

  const getSourceBadge = (type: string) => {
    switch (type) {
      case 'admin':
        return <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">관리자</span>;
      case 'crawled':
        return <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded">크롤링</span>;
      case 'voucher':
        return <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">바우처</span>;
      default:
        return null;
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-2 text-sm font-medium rounded-md ${
            i === currentPage
              ? 'bg-kahan-primary text-white'
              : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-8">
        {currentPage > 1 && (
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            이전
          </button>
        )}
        {pages}
        {currentPage < totalPages && (
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            다음
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">전체 상품</h1>
        <p className="text-gray-600">KAHAN Mall의 모든 상품을 만나보세요</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleFilterChange()}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-kahan-primary focus:border-kahan-primary"
              placeholder="상품명으로 검색..."
            />
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">카테고리</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                handleFilterChange();
              }}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-kahan-primary focus:border-kahan-primary"
            >
              <option value="">전체 카테고리</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.productCount})
                </option>
              ))}
            </select>
          </div>

          {/* Payment Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">결제 타입</label>
            <select
              value={selectedPaymentType}
              onChange={(e) => {
                setSelectedPaymentType(e.target.value);
                handleFilterChange();
              }}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-kahan-primary focus:border-kahan-primary"
            >
              <option value="">전체 결제 타입</option>
              <option value="cash_only">Cash Point 전용</option>
              <option value="gold_only">Gold Point 전용</option>
              <option value="mixed">혼합 결제</option>
            </select>
          </div>

          {/* Sort Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">정렬</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                handleFilterChange();
              }}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-kahan-primary focus:border-kahan-primary"
            >
              <option value="newest">최신순</option>
              <option value="price_asc">가격 낮은순</option>
              <option value="price_desc">가격 높은순</option>
              <option value="name_asc">이름순</option>
            </select>
          </div>

          {/* View Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">보기 방식</label>
            <div className="flex border border-gray-300 rounded-md">
              <button
                onClick={() => setIsGridView(true)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-l-md ${
                  isGridView
                    ? 'bg-kahan-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4 mx-auto" />
              </button>
              <button
                onClick={() => setIsGridView(false)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-r-md border-l ${
                  !isGridView
                    ? 'bg-kahan-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ListBulletIcon className="h-4 w-4 mx-auto" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">
          총 <span className="font-medium text-gray-900">{totalItems.toLocaleString()}</span>개의 상품
        </p>
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <span className="text-sm text-gray-600">필터 적용됨</span>
        </div>
      </div>

      {/* Products Grid/List */}
      {isLoading ? (
        <div className={`grid gap-6 ${isGridView ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
          {[...Array(12)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
              <div className="bg-gray-300 h-48 rounded mb-4"></div>
              <div className="bg-gray-300 h-4 rounded mb-2"></div>
              <div className="bg-gray-300 h-4 rounded w-2/3 mb-2"></div>
              <div className="bg-gray-300 h-4 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">상품이 없습니다</h3>
          <p className="text-gray-600 mb-4">검색 조건을 변경하여 다시 시도해보세요</p>
          <button
            onClick={() => {
              setSelectedCategory('');
              setSelectedPaymentType('');
              setSearchQuery('');
              setSortBy('newest');
              handleFilterChange();
            }}
            className="text-kahan-primary font-medium hover:text-blue-700"
          >
            필터 초기화
          </button>
        </div>
      ) : (
        <>
          <div className={`grid gap-6 ${isGridView ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className={`bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group ${
                  isGridView ? '' : 'flex'
                }`}
              >
                <div className={`${isGridView ? 'aspect-w-1 aspect-h-1 w-full' : 'w-48 flex-shrink-0'} overflow-hidden bg-gray-200`}>
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className={`${isGridView ? 'w-full h-48' : 'w-full h-32'} object-cover group-hover:scale-105 transition-transform duration-300`}
                    />
                  ) : (
                    <div className={`${isGridView ? 'w-full h-48' : 'w-full h-32'} bg-gray-200 flex items-center justify-center`}>
                      <CubeIcon className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className={`p-4 ${isGridView ? '' : 'flex-1'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {product.category}
                    </span>
                    <div className="flex space-x-1">
                      {getPaymentTypeBadge(product.paymentType)}
                      {getSourceBadge(product.sourceType)}
                    </div>
                  </div>
                  <h3 className={`font-medium text-gray-900 mb-2 ${isGridView ? 'text-sm line-clamp-2' : 'text-lg'}`}>
                    {product.name}
                  </h3>
                  {!isGridView && product.description && (
                    <p className="text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                  )}
                  <p className={`font-semibold text-kahan-primary ${isGridView ? 'text-sm' : 'text-lg'}`}>
                    {formatPrice(product.cashPointPrice, product.goldPointPrice)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Pagination */}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default ProductsPage;